<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import type { ListTicketDetail } from '@/common/api/models.pleased';
import { useGetPageField } from '@/common/customisation/composables';
import {
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  combineStyles,
} from '@/common/customisation/utils/styleUtils';
import { getImageFieldUrl } from '@/common/customisation/utils/imageFieldValue';
import { PUI } from '@pleased-npm/fe-customisation';
import TimeAgo from '@/components/TimeAgo.vue';

const defaultLogoUrl = require('@/assets/img/shared/default/logo.png') as string;

interface Props {
  ticket?: ListTicketDetail;
  textColor?: string;
  subTextColor?: string;
  backgroundColor?: string;
  borderColor?: string | null;
  backgroundColorTagResolved?: string;
  backgroundColorTagClosed?: string;
  textColorTagResolved?: string;
  textColorTagClosed?: string;
  noTag?: boolean;
  /** When true, shows the recently-resolved dot (parent decides, e.g. closed/resolved list). */
  showRecentResolvedIndicator?: boolean;
}

const props = defineProps<Props>();
const router = useRouter();

const chatIconField = useGetPageField({
  registryId: PUI.REGISTRY_IDS.DEFAULT_THEME,
  blockKey: PUI.THEME_BLOCK_KEYS.BRAND_ASSETS,
  sectionKey: PUI.THEME_SECTION_KEYS.CHATBOT_CHAT_ICON,
  fieldKey: PUI.THEME_FIELD_KEYS.CHAT_ICON,
});

const conversationIconUrl = computed(() => getImageFieldUrl(chatIconField.value) || defaultLogoUrl);

const displayTicketNumber = computed(() => {
  return props.ticket ? `#${props.ticket.id}` : undefined;
});

const handleClick = () => {
  if (props.ticket?.id) {
    router.push({
      name: 'conversation-details',
      params: { id: String(props.ticket.id) },
      query: { status: props.ticket.status },
    });
  }
};

const displayTitle = computed(() => {
  return props.ticket?.subject || 'Conversation';
});

const displayPreview = computed(() => {
  const raw = props.ticket?.lastMessagePreview?.trim();
  return raw || '';
});

const displayTag = computed(() => {
  if (!props.ticket || props.noTag) return undefined;

  const status = props.ticket.status?.toLowerCase();
  if (status) return status;
  return undefined;
});

const updatedAt = computed(() => {
  return props.ticket?.updatedAt ? new Date(props.ticket.updatedAt) : null;
});

const containerStyle = computed(() =>
  combineStyles(createBackgroundStyle(props.backgroundColor), createBorderStyle(props.borderColor))
);

const textStyle = computed(() => {
  const style = createTextStyle(props.textColor);
  style.maxWidth = props.noTag ? '90%' : '60%';
  return style;
});

const subTextStyle = computed(() => createTextStyle(props.subTextColor));

const TAG_STATUS = {
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

const tagStyle = computed(() => {
  if (!displayTag.value) return {};

  const tag = displayTag.value;

  if (tag === TAG_STATUS.RESOLVED) {
    return combineStyles(
      createBackgroundStyle(props.backgroundColorTagResolved),
      createTextStyle(props.textColorTagResolved)
    );
  }

  if (tag === TAG_STATUS.CLOSED) {
    return combineStyles(
      createBackgroundStyle(props.backgroundColorTagClosed),
      createTextStyle(props.textColorTagClosed)
    );
  }

  return {};
});

const recentResolvedIndicatorStyle = computed(() => createBackgroundStyle(props.backgroundColorTagResolved));
</script>

<template>
  <div
    class="card-radius conversation-item"
    :style="containerStyle"
    tabindex="0"
    role="button"
    aria-label="Open conversation"
    @click="handleClick"
  >
    <span
      v-if="showRecentResolvedIndicator"
      class="recently-resolved-indicator"
      aria-hidden="true"
      :style="recentResolvedIndicatorStyle"
    />
    <div class="conversation-item-content">
      <img
        :src="conversationIconUrl"
        alt="Conversation icon"
        class="conversation-logo"
        data-testid="conversation-item-icon"
      />
      <div class="conversation-content">
        <div class="conversation-header">
          <h3 class="conversation-title theme-font-main" :style="textStyle">
            {{ displayTitle }}
          </h3>
          <span v-if="displayTag" class="card-radius conversation-tag" :style="tagStyle">
            {{ displayTag }}
          </span>
        </div>
        <p v-if="displayPreview" class="conversation-preview theme-font-main" :style="subTextStyle">
          {{ displayPreview }}
        </p>
        <div class="conversation-meta">
          <span v-if="displayTicketNumber" class="theme-font-main" :style="subTextStyle">
            {{ displayTicketNumber }}
          </span>
          <span class="theme-font-main" :style="subTextStyle">
            <TimeAgo :date="updatedAt" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.conversation-item {
  position: relative;
  padding: 16px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
}

.recently-resolved-indicator {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  pointer-events: none;
}

.conversation-logo {
  width: 25px;
  height: 25px;
  border-radius: 50%;
}

.conversation-item-content {
  display: flex;
  gap: 12px;
}

.conversation-content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  padding-right: 12px;
}

.conversation-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.conversation-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-preview {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.35;
  opacity: 0.92;

  // Hardcoded font weight for the preview text
  font-weight: 700;
}

.conversation-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.conversation-message {
  margin-bottom: 8px;
}

.conversation-tag {
  padding: 4px 8px;
  text-transform: capitalize;
  white-space: nowrap;
}
</style>
